/**
 * Cross-Selling Module
 * Enhanced functionality for cross-selling sections
 */

export class CrossSelling {
  constructor(container) {
    this.container = container;
    this.addButtons = container.querySelectorAll('[data-add-to-cart-cross-sell]');
    
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.addButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.productHandle;
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Show loading state
    this.setButtonState(button, 'loading');

    try {
      const result = await this.addToCart(variantId);
      
      if (result.success) {
        this.setButtonState(button, 'success');
        this.triggerCartUpdate(result.data, button);
        
        // Reset button after delay
        setTimeout(() => {
          this.setButtonState(button, 'default');
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Cross-sell add to cart error:', error);
      this.setButtonState(button, 'error');
      
      // Reset button after delay
      setTimeout(() => {
        this.setButtonState(button, 'default');
      }, 2000);
    }
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.description || data.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  setButtonState(button, state) {
    const btnText = button.querySelector('.dsx-cross-selling__add-btn-text');
    const btnLoader = button.querySelector('.dsx-cross-selling__add-btn-loader');
    
    // Store original text if not already stored
    if (!button.dataset.originalText && btnText) {
      button.dataset.originalText = btnText.textContent;
    }

    switch (state) {
      case 'loading':
        button.disabled = true;
        if (btnText) btnText.style.display = 'none';
        if (btnLoader) btnLoader.style.display = 'inline-block';
        break;

      case 'success':
        button.disabled = true;
        if (btnText) {
          btnText.textContent = this.getTranslation('added', 'Hinzugefügt!');
          btnText.style.display = 'inline-block';
        }
        if (btnLoader) btnLoader.style.display = 'none';
        break;

      case 'error':
        button.disabled = false;
        if (btnText) {
          btnText.textContent = this.getTranslation('error', 'Fehler');
          btnText.style.display = 'inline-block';
        }
        if (btnLoader) btnLoader.style.display = 'none';
        break;

      case 'default':
      default:
        button.disabled = false;
        if (btnText) {
          btnText.textContent = button.dataset.originalText || this.getTranslation('add_to_cart', 'In den Warenkorb');
          btnText.style.display = 'inline-block';
        }
        if (btnLoader) btnLoader.style.display = 'none';
        break;
    }
  }

  triggerCartUpdate(product, button) {
    // Trigger Shopify theme event for cart drawer/page update
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: product,
        addToCartBtn: button
      },
      bubbles: true
    }));
  }

  getTranslation(key, fallback) {
    // Try to get translation from Shopify theme translations
    if (window.theme && window.theme.strings && window.theme.strings[key]) {
      return window.theme.strings[key];
    }
    return fallback;
  }
}

/**
 * Auto-initialize cross-selling sections
 */
export function initCrossSelling() {
  const sections = document.querySelectorAll('[data-section-type="cross-selling"]');
  
  sections.forEach(section => {
    new CrossSelling(section);
  });
}

// Auto-init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCrossSelling);
} else {
  initCrossSelling();
}

// Re-init on Shopify section load (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-section-type="cross-selling"]')) {
      initCrossSelling();
    }
  });
}

